import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeftIcon,
  ChartBarIcon,
  ScaleIcon,
  ClockIcon,
  CpuChipIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import TradingViewWidget from '../../components/common/TradingViewWidget'
import { Recommendation } from '../../types'
import { recommendationsApi } from '../../services/api'

const RecommendationDetailsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedTimeframe, setSelectedTimeframe] = useState('1Y')

  useEffect(() => {
    if (slug) {
      fetchRecommendation(slug)
    }
  }, [slug])

  const fetchRecommendation = async (recommendationId: string) => {
    try {
      setLoading(true)
      const data = await recommendationsApi.getRecommendation(recommendationId)
      setRecommendation(data)
    } catch (error) {
      console.error('Error fetching recommendation:', error)
      setRecommendation(null)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '₹0.00'
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined || isNaN(Number(value))) {
      return '0.00%'
    }
    const numValue = Number(value)
    return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(2)}%`
  }

  const getAnalysisIcon = (type: string) => {
    switch (type) {
      case 'Bullish':
        return <ArrowTrendingUpIcon className="h-8 w-8 text-emerald-400" />
      case 'Balanced':
        return <ScaleIcon className="h-8 w-8 text-blue-400" />
      case 'Short-Term':
        return <ClockIcon className="h-8 w-8 text-purple-400" />
      case 'AI-Assisted':
        return <CpuChipIcon className="h-8 w-8 text-indigo-400" />
      default:
        return <ChartBarIcon className="h-8 w-8 text-gray-400" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!recommendation) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800">
        <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Recommendation Not Found</h2>
        <p className="text-gray-400 mb-6">The recommendation you're looking for doesn't exist or has been removed.</p>
        <Button onClick={() => navigate('/dashboard/recommendations')} variant="primary">
          Back to Recommendations
        </Button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 text-white">
      {/* Premium Header with Gradient */}
      <div className="relative bg-gradient-to-r from-slate-900/95 via-gray-900/95 to-slate-800/95 backdrop-blur-sm border-b border-gray-700/50 px-8 py-6">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5"></div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Button
              onClick={() => navigate('/dashboard/recommendations')}
              variant="ghost"
              className="text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 backdrop-blur-sm transition-all duration-200 px-4 py-2 rounded-xl"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back
            </Button>
            <div className="space-y-1">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                {recommendation.company_name}
              </h1>
              <div className="flex items-center space-x-3">
                <p className="text-gray-400 text-base font-medium">{recommendation.exchange}: {recommendation.symbol}</p>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                  recommendation.recommendation_type.toUpperCase() === 'BUY'
                    ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
                    : recommendation.recommendation_type.toUpperCase() === 'SELL'
                    ? 'text-red-400 bg-red-500/10 border-red-500/30'
                    : 'text-amber-400 bg-amber-500/10 border-amber-500/30'
                }`}>
                  {recommendation.recommendation_type}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right space-y-2">
            <div className="text-4xl font-bold bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">
              {formatCurrency(recommendation.current_price || recommendation.entry_price)}
            </div>
            <div className={`text-xl font-semibold flex items-center justify-end space-x-2 ${
              (recommendation.performance || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {(recommendation.performance || 0) >= 0 ? 
                <ArrowTrendingUpIcon className="h-5 w-5" /> : 
                <ArrowTrendingDownIcon className="h-5 w-5" />
              }
              <span>{formatPercentage(recommendation.performance)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative px-8 py-8">{/* Main container with premium background */}
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/3 via-purple-600/2 to-slate-900/50 pointer-events-none"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        
        <div className="relative grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-8">
            {/* Premium Summary Card */}
            <Card className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 backdrop-blur-xl shadow-2xl">
              <div className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                      <div className="w-1 h-6 bg-gradient-to-b from-blue-400 to-purple-500 rounded-full mr-3"></div>
                      Investment Summary
                    </h3>
                    <p className="text-gray-300 text-lg leading-relaxed font-light">
                      {recommendation.summary}
                    </p>
                  </div>
                  <div className="ml-6">
                    <button
                      className={`px-8 py-3 rounded-2xl font-semibold text-sm border-2 transition-all duration-300 hover:scale-105 ${
                        recommendation.recommendation_type.toUpperCase() === 'BUY'
                          ? 'text-emerald-300 bg-gradient-to-r from-emerald-600/20 to-emerald-500/20 border-emerald-400/50 hover:bg-gradient-to-r hover:from-emerald-600/30 hover:to-emerald-500/30 shadow-lg shadow-emerald-500/20'
                          : recommendation.recommendation_type.toUpperCase() === 'SELL'
                          ? 'text-red-300 bg-gradient-to-r from-red-600/20 to-red-500/20 border-red-400/50 hover:bg-gradient-to-r hover:from-red-600/30 hover:to-red-500/30 shadow-lg shadow-red-500/20'
                          : 'text-amber-300 bg-gradient-to-r from-amber-600/20 to-amber-500/20 border-amber-400/50 hover:bg-gradient-to-r hover:from-amber-600/30 hover:to-amber-500/30 shadow-lg shadow-amber-500/20'
                      }`}
                    >
                      {recommendation.recommendation_type}
                    </button>
                  </div>
                </div>
              </div>
            </Card>

            {/* Premium Analysis Overview */}
            <Card className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 backdrop-blur-xl shadow-2xl">
              <div className="p-8">
                <h2 className="text-2xl font-semibold text-white mb-8 flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-purple-400 to-blue-500 rounded-full mr-3"></div>
                  Analysis Overview
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {[
                    { label: 'Bullish Sentiment', icon: 'Bullish', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20', hoverBorderColor: 'hover:border-emerald-400/40' },
                    { label: 'Risk Balanced', icon: 'Balanced', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/20', hoverBorderColor: 'hover:border-blue-400/40' },
                    { label: 'Short-Term Focus', icon: 'Short-Term', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/20', hoverBorderColor: 'hover:border-purple-400/40' },
                    { label: 'AI-Powered', icon: 'AI-Assisted', bgColor: 'bg-indigo-500/10', borderColor: 'border-indigo-500/20', hoverBorderColor: 'hover:border-indigo-400/40' }
                  ].map((item, index) => (
                    <div key={index} className="text-center group cursor-pointer">
                      <div className={`flex justify-center mb-4 p-4 rounded-2xl ${item.bgColor} border ${item.borderColor} ${item.hoverBorderColor} transition-all duration-300 group-hover:scale-105`}>
                        {getAnalysisIcon(item.icon)}
                      </div>
                      <p className="text-gray-300 text-sm font-medium group-hover:text-white transition-colors duration-200">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Premium Price Chart */}
            <Card className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 backdrop-blur-xl shadow-2xl">
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-semibold text-white flex items-center">
                    <div className="w-1 h-6 bg-gradient-to-b from-green-400 to-blue-500 rounded-full mr-3"></div>
                    Interactive Price Chart
                  </h2>
                  <div className="flex space-x-2 bg-white/5 rounded-xl p-1 backdrop-blur-sm border border-white/10">
                    {['1D', '1M', '6M', '1Y'].map((timeframe) => (
                      <button
                        key={timeframe}
                        onClick={() => setSelectedTimeframe(timeframe)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          selectedTimeframe === timeframe
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25'
                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        {timeframe}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Enhanced TradingView Chart Widget */}
                <div className="h-96 bg-gradient-to-br from-slate-900/50 to-gray-900/50 rounded-2xl border border-white/10 overflow-hidden shadow-inner">
                  <TradingViewWidget
                    symbol={`NSE:${recommendation.symbol}`}
                    theme="dark"
                    height={384}
                    interval={selectedTimeframe === '1D' ? '1' : selectedTimeframe === '1M' ? '1D' : selectedTimeframe === '6M' ? '1W' : '1M'}
                    toolbar_bg="#0f172a"
                    style="1"
                    locale="en"
                    timezone="Asia/Kolkata"
                    enable_publishing={false}
                    allow_symbol_change={false}
                  />
                </div>
              </div>
            </Card>

            {/* Premium Technical Indicators */}
            <Card className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 backdrop-blur-xl shadow-2xl">
              <div className="p-8">
                <h2 className="text-2xl font-semibold text-white mb-8 flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-red-500 rounded-full mr-3"></div>
                  Technical Analysis
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-emerald-400 rounded-full"></div>
                          <span className="text-gray-300 font-medium">RSI Signal</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <ArrowTrendingUpIcon className="h-5 w-5 text-emerald-400" />
                          <span className="text-emerald-400 font-semibold text-lg">
                            {recommendation.rsi_signal || 'Bullish'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                          <span className="text-gray-300 font-medium">MACD</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-blue-400 font-semibold text-lg">
                            +{recommendation.macd ? Number(recommendation.macd).toFixed(2) : '9.73'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
                          <span className="text-gray-300 font-medium">RSI Value</span>
                        </div>
                        <span className="text-white font-semibold text-lg">
                          {recommendation.rsi ? Number(recommendation.rsi).toFixed(1) : '65.8'}
                        </span>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
                          <span className="text-gray-300 font-medium">Signal</span>
                        </div>
                        <span className="text-orange-400 font-semibold text-lg">
                          {recommendation.macd_signal || 'Bullish'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Premium Sidebar */}
          <div className="space-y-8">
            {/* Premium Recommendation Card */}
            <Card className="bg-gradient-to-br from-teal-600/20 to-cyan-600/10 border border-teal-500/30 backdrop-blur-xl shadow-2xl shadow-teal-500/10">
              <div className="p-8 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-cyan-500/5"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg shadow-teal-500/25">
                    <ChartBarIcon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Investment Recommendation</h3>
                  <Button className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-semibold py-4 px-10 rounded-2xl w-full shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-all duration-300 hover:scale-105 border border-teal-400/30">
                    {recommendation.recommendation_type}
                  </Button>
                  <p className="text-teal-100 text-sm mt-6 leading-relaxed font-light">
                    {recommendation.detailed_analysis}
                  </p>
                </div>
              </div>
            </Card>

            {/* Premium Price Targets */}
            <Card className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 backdrop-blur-xl shadow-2xl">
              <div className="p-8">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-emerald-400 to-green-500 rounded-full mr-3"></div>
                  Price Targets
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50">
                    <span className="text-gray-300 font-medium">Entry Price</span>
                    <span className="text-white font-semibold text-lg">
                      {formatCurrency(recommendation.entry_price)}
                    </span>
                  </div>
                  {recommendation.target_1 && (
                    <div className="flex justify-between items-center p-4 bg-emerald-900/20 backdrop-blur-sm rounded-xl border border-emerald-500/30">
                      <span className="text-gray-300 font-medium">Target 1</span>
                      <span className="text-emerald-400 font-semibold text-lg">
                        {formatCurrency(recommendation.target_1)}
                      </span>
                    </div>
                  )}
                  {recommendation.target_2 && (
                    <div className="flex justify-between items-center p-4 bg-emerald-900/20 backdrop-blur-sm rounded-xl border border-emerald-500/30">
                      <span className="text-gray-300 font-medium">Target 2</span>
                      <span className="text-emerald-400 font-semibold text-lg">
                        {formatCurrency(recommendation.target_2)}
                      </span>
                    </div>
                  )}
                  {recommendation.target_3 && (
                    <div className="flex justify-between items-center p-4 bg-emerald-900/20 backdrop-blur-sm rounded-xl border border-emerald-500/30">
                      <span className="text-gray-300 font-medium">Target 3</span>
                      <span className="text-emerald-400 font-semibold text-lg">
                        {formatCurrency(recommendation.target_3)}
                      </span>
                    </div>
                  )}
                  {recommendation.stop_loss && (
                    <div className="flex justify-between items-center p-4 bg-red-900/20 backdrop-blur-sm rounded-xl border border-red-500/30 mt-6">
                      <span className="text-gray-300 font-medium">Stop Loss</span>
                      <span className="text-red-400 font-semibold text-lg">
                        {formatCurrency(recommendation.stop_loss)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Premium Risk & Timeline */}
            <Card className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 backdrop-blur-xl shadow-2xl">
              <div className="p-8">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-purple-400 to-pink-500 rounded-full mr-3"></div>
                  Risk Assessment
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-gradient-to-r from-white/5 to-white/3 rounded-xl border border-white/10">
                    <span className="text-gray-300 font-medium">Risk Level</span>
                    <span className={`font-semibold px-4 py-2 rounded-xl text-sm border ${
                      recommendation.risk_level === 'Low' ? 'bg-gradient-to-r from-emerald-500/20 to-green-500/10 text-emerald-400 border-emerald-500/30' :
                      recommendation.risk_level === 'Medium' ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/10 text-amber-400 border-amber-500/30' :
                      'bg-gradient-to-r from-red-500/20 to-red-600/10 text-red-400 border-red-500/30'
                    }`}>
                      {recommendation.risk_level}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gradient-to-r from-white/5 to-white/3 rounded-xl border border-white/10">
                    <span className="text-gray-300 font-medium">Time Horizon</span>
                    <span className="text-white font-semibold">
                      {recommendation.time_horizon}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gradient-to-r from-emerald-500/10 to-green-500/5 rounded-xl border border-emerald-500/20">
                    <span className="text-gray-300 font-medium">Status</span>
                    <span className="flex items-center text-emerald-400 font-semibold">
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      {recommendation.status}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Premium Performance Card */}
            <Card className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 backdrop-blur-xl shadow-2xl">
              <div className="p-8 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5"></div>
                <div className="relative">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center justify-center">
                    <div className="w-1 h-6 bg-gradient-to-b from-blue-400 to-purple-500 rounded-full mr-3"></div>
                    Performance
                  </h3>
                  <div className="relative">
                    <div className={`text-4xl font-bold mb-4 bg-gradient-to-r ${
                      (recommendation.performance || 0) >= 0 
                        ? 'from-emerald-400 to-green-500' 
                        : 'from-red-400 to-red-500'
                    } bg-clip-text text-transparent`}>
                      {formatPercentage(recommendation.performance)}
                    </div>
                    <div className={`inline-flex items-center px-4 py-2 rounded-xl border ${
                      (recommendation.performance || 0) >= 0 
                        ? 'bg-gradient-to-r from-emerald-500/20 to-green-500/10 border-emerald-500/30 text-emerald-400' 
                        : 'bg-gradient-to-r from-red-500/20 to-red-600/10 border-red-500/30 text-red-400'
                    }`}>
                      {(recommendation.performance || 0) >= 0 ? 
                        <ArrowTrendingUpIcon className="h-5 w-5 mr-2" /> : 
                        <ArrowTrendingDownIcon className="h-5 w-5 mr-2" />
                      }
                      <span className="font-medium">Since Entry</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RecommendationDetailsPage
